import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  DragDropContext,
  Droppable,
  DropResult,
  Draggable,
} from "react-beautiful-dnd";
import { useParams, useHistory } from "react-router-dom";
import Container from "../../atoms/page/Page";
import InlineGroup from "../../atoms/inlinegroup/InlineGroup";
import LoadingSpinner from "../../atoms/loadingspinner/LoadingSpinner";
import ReportingTemplateDelete from "./ReportingTemplateDelete";
import Banner from "../../atoms/banner/Banner";
import IconButton from "../../molecules/iconbutton/IconButton";
import { useReportingTemplate } from "../../../hooks/useReportingTemplate";
import { CloneReportingTemplate } from "./actions/CloneReportingTemplate";
import VerticalGroup from "../../atoms/verticalgroup/VerticalGroup";
import RoundResultsPage from "../results/RoundResultsPage";
import Button from "../../atoms/button/Button";
import useElementSize from "../../../hooks/useElementSize";
import api from "../../../services/api";
import Clickable from "../../atoms/clickable/Clickable";
import ContentStretcher from "./components/ContentStretcher";
import ReportingPagePreviewTile, {
  THUMBNAIL_COLUMN_WIDTH,
  RENDERED_DIMENSIONS,
} from "./components/ReportingPagePreviewTile";
import { AddReportingTemplateLayout } from "./actions/AddReportingTemplateLayout";
import { EditRoundRestrictions } from "./actions/EditRoundRestrictions";
import { AutoConfigure } from "./actions/Autoconfigure";
import { GenericPreview } from "./actions/GenericPreview";
import DeleteReportingTemplateLayout from "./ReportingTemplateDeleteLayout";
import Text from "../../atoms/text/Text";
import { useGenericPreviewReportingData } from "../../../hooks/usePreviewReportingData";
import Toggle from "../../atoms/toggle/Toggle";
import { DurationEstimateEdit } from "./actions/DurationEstimateEdit";
import { EditSpeakerNotes } from "./actions/EditSpeakerNotes";

interface AssessmentState {
  deleteModalOpen: boolean;
  deleteLayoutModalOpen: boolean;
}

const SIDEBAR_WIDTH = 230;
const HEADING_HEIGHT = 64;
function ReportingTemplateDetails() {
  const { reportingTemplateId } = useParams<{ reportingTemplateId: string }>();
  const history = useHistory();
  const [apiError, setApiError] = useState<string | null>(null);
  const [selectedLayout, setSelectedLayout] =
    useState<API.ReportingTemplateLayoutResponse | null>(null);
  const [apiUpdating, setApiUpdating] = useState(false);
  const [layouts, setLayouts] = useState<API.ReportingTemplateLayoutResponse[]>(
    [],
  );
  const [containerRef, { height: containerHeight, width: containerWidth }] =
    useElementSize();

  const { previewHeight, previewWidth } = useMemo(() => {
    if (containerHeight) {
      const heightAvailable = containerHeight;
      const widthAvailable =
        containerWidth - THUMBNAIL_COLUMN_WIDTH - SIDEBAR_WIDTH;

      const scale = Math.min(
        1,
        Math.min(
          widthAvailable / RENDERED_DIMENSIONS.width,
          heightAvailable / RENDERED_DIMENSIONS.height,
        ),
      );

      return {
        previewHeight: scale * RENDERED_DIMENSIONS.height,
        previewWidth: scale * RENDERED_DIMENSIONS.width,
      };
    }
    return { previewHeight: 0, previewWidth: 0 };
  }, [containerHeight, containerWidth]);

  const { inProgress, data, error, refresh } =
    useReportingTemplate(reportingTemplateId);
  const { data: previewData } = useGenericPreviewReportingData(
    reportingTemplateId,
    3,
  );

  useEffect(() => {
    if (data?.layouts) {
      setLayouts(data.layouts);
      if (
        !selectedLayout ||
        !data.layouts.some((layout) => layout.id === selectedLayout.id)
      ) {
        setSelectedLayout(data.layouts[0]);
      }
    }
  }, [data?.layouts, selectedLayout]);

  const [{ deleteModalOpen, deleteLayoutModalOpen }, setUIState] =
    useState<AssessmentState>({
      deleteModalOpen: false,
      deleteLayoutModalOpen: false,
    });

  const onDeleteComplete = useCallback(() => {
    setUIState((prevState) => ({ ...prevState, deleteModalOpen: false }));
    history.push("/reporting-templates");
  }, [history]);

  const onDeleteLayoutComplete = useCallback(() => {
    setUIState((prevState) => ({ ...prevState, deleteLayoutModalOpen: false }));
    refresh();
  }, [refresh]);

  const onLayoutUpdate = useCallback(
    async (
      layoutId: string,
      data: API.ReportingTemplateLayoutUpdateRequest,
    ) => {
      try {
        setApiUpdating(true);
        if (selectedLayout?.id === layoutId) {
          setSelectedLayout((sl) => ({
            ...sl!,
            ...data,
          }));
        }
        setLayouts((list) => {
          const result = Array.from(list);
          const index = result.findIndex((r) => r.id === layoutId);
          result[index] = { ...result[index], ...data };
          return result;
        });
        await api.updateReportingTemplateLayout(
          reportingTemplateId,
          layoutId,
          data,
        );
        refresh();
        setApiUpdating(false);
      } catch (e) {
        setApiUpdating(false);
        setApiError(e.message);
      }
    },
    [selectedLayout, reportingTemplateId, refresh],
  );

  const onDragEnd = useCallback(
    async (result: DropResult) => {
      if (!result.destination) return;

      const sourceIndex = result.source.index;
      const destIndex = result.destination.index;

      let layoutsResult: API.ReportingTemplateLayoutResponse[] = [];

      setLayouts((list) => {
        const result = Array.from(list);
        const [removed] = result.splice(sourceIndex, 1);
        result.splice(destIndex, 0, removed);
        layoutsResult = result;
        return result;
      });

      for (let i = 0; i < layoutsResult.length; i++) {
        const val = layoutsResult[i];
        if (val.config && val.config.type === "custom_image") {
          delete val.config.imageUrl;
        }
        if (val.config && val.config.type === "custom_text_and_image_left") {
          delete val.config.imageUrl;
        }
        if (val.config && val.config.type === "custom_text_and_image_right") {
          delete val.config.imageUrl;
        }
        if (val.config && val.config.type === "custom_video") {
          delete val.config.videoUrl;
        }
      }

      try {
        setApiUpdating(true);
        await api.patchReportingTemplate(reportingTemplateId, {
          layouts: layoutsResult,
        });
        refresh();
        setApiUpdating(false);
      } catch (e) {
        setApiUpdating(false);
        setApiError(e.message);
      }
    },
    [refresh, reportingTemplateId],
  );

  const onCustomTemplateEdit = useCallback(
    (val: API.ReportingTemplateLayoutUpdateRequest) => {
      if (selectedLayout?.id) {
        if (val.config && val.config.type === "custom_image") {
          delete val.config.imageUrl;
        }
        if (val.config && val.config.type === "custom_text_and_image_left") {
          delete val.config.imageUrl;
        }
        if (val.config && val.config.type === "custom_text_and_image_right") {
          delete val.config.imageUrl;
        }
        if (val.config && val.config.type === "custom_video") {
          delete val.config.videoUrl;
        }
        onLayoutUpdate(selectedLayout.id, val);
      }
    },
    [onLayoutUpdate, selectedLayout?.id],
  );

  return (
    <>
      {deleteModalOpen && !!data && (
        <ReportingTemplateDelete
          isOpen={deleteModalOpen}
          onClose={() =>
            setUIState((prevState) => ({
              ...prevState,
              deleteModalOpen: false,
            }))
          }
          onComplete={onDeleteComplete}
          template={data}
        />
      )}
      {deleteLayoutModalOpen && !!selectedLayout && (
        <DeleteReportingTemplateLayout
          isOpen={deleteLayoutModalOpen}
          onClose={() =>
            setUIState((prevState) => ({
              ...prevState,
              deleteLayoutModalOpen: false,
            }))
          }
          onComplete={onDeleteLayoutComplete}
          reportingTemplateId={reportingTemplateId}
          layout={selectedLayout}
        />
      )}

      <Container className="template-edit" noOverflow>
        {error && (
          <Banner type="error" active={!!error} message={error?.message} />
        )}
        {apiError != null && (
          <Banner type="error" active={!!apiError} message={apiError} />
        )}
        <InlineGroup spread verticalCenter>
          <h2>{data?.name ?? "Template Details"}</h2>
          {!!data && !!previewData && (
            <InlineGroup spread spaceBetweenElements={2}>
              <GenericPreview reportingTemplateId={data.id} />
              <AutoConfigure
                reportingTemplateId={data.id}
                onFinish={() => refresh()}
              />
              <AddReportingTemplateLayout
                template={data}
                onFinish={() => refresh()}
                previewData={previewData}
              />
              <CloneReportingTemplate template={data} type="button" />
              <IconButton
                data-test={`edit-template-${reportingTemplateId}`}
                icon="edit"
                text="Edit Template"
                linkTo={`/reporting-templates/${reportingTemplateId}/edit`}
              />
              <IconButton
                danger
                data-test={`delete-template-${data.id}`}
                icon="trash"
                text="Delete"
                onClick={() =>
                  setUIState((prevState) => ({
                    ...prevState,
                    deleteModalOpen: true,
                  }))
                }
              />
            </InlineGroup>
          )}
        </InlineGroup>
        {inProgress && !data && <LoadingSpinner />}
        {!!data && !!previewData && (
          <InlineGroup
            ref={containerRef}
            className="mb-4"
            fullHeight
            spaceBetweenElements={2}
          >
            {containerHeight > 0 && (
              <>
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId={reportingTemplateId}>
                    {(provided) => (
                      <VerticalGroup
                        spaceBetweenElements={2}
                        style={{
                          overflowY: "auto",
                          maxHeight: containerHeight - HEADING_HEIGHT,
                          width: THUMBNAIL_COLUMN_WIDTH,
                        }}
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                      >
                        {layouts.map((l, index) => (
                          <Draggable
                            key={l.id}
                            draggableId={l.id}
                            index={index}
                          >
                            {(provided) => {
                              return (
                                <Clickable
                                  // @ts-expect-error ref does exist
                                  ref={provided.innerRef}
                                  style={{ ...provided.draggableProps.style }}
                                  onClick={() => setSelectedLayout(l)}
                                  {...provided.draggableProps}
                                >
                                  <ReportingPagePreviewTile
                                    data={{
                                      ...previewData,
                                      layouts: [{ ...l, enabled: true }],
                                    }}
                                    disabled={apiUpdating}
                                    layout={l}
                                    onLayoutUpdate={onLayoutUpdate}
                                    dragHandleProps={
                                      provided.dragHandleProps || undefined
                                    }
                                  />
                                </Clickable>
                              );
                            }}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </VerticalGroup>
                    )}
                  </Droppable>
                </DragDropContext>
                <div
                  style={{ flex: 1, width: "100%", height: containerHeight }}
                >
                  {selectedLayout && (
                    <InlineGroup
                      spaceBetweenElements={2}
                      style={{
                        flex: 1,
                        height: containerHeight,
                        width: "96%",
                      }}
                      spread
                      center
                    >
                      {previewHeight && previewWidth && (
                        <ContentStretcher
                          style={{
                            background: "#e1e1e1",
                            width: previewWidth,
                            height: previewHeight,
                            minHeight: previewHeight,
                          }}
                          contentWidth={RENDERED_DIMENSIONS.width}
                          contentHeight={RENDERED_DIMENSIONS.height}
                        >
                          <div
                            style={{
                              width: RENDERED_DIMENSIONS.width,
                              height: RENDERED_DIMENSIONS.height,
                              minHeight: RENDERED_DIMENSIONS.height,
                              position: "absolute",
                            }}
                          >
                            <RoundResultsPage
                              key={selectedLayout.id}
                              data={{
                                ...previewData,
                                layouts: [{ ...selectedLayout, enabled: true }],
                              }}
                              roundNumber={3}
                              onlyThesePages={[selectedLayout?.type]}
                              hidePagination
                              hideTeamPositions
                              allowCustomTemplateEdits
                              onCustomTemplateEdit={onCustomTemplateEdit}
                              ignoreKeyPress
                            />
                          </div>
                        </ContentStretcher>
                      )}
                      <VerticalGroup
                        style={{ width: SIDEBAR_WIDTH - 50 }}
                        spaceBetweenElements={2}
                      >
                        <Text bold>Slide Controls</Text>
                        <DurationEstimateEdit
                          reportingTemplateId={reportingTemplateId}
                          layout={selectedLayout}
                          onFinish={() => refresh()}
                        />
                        <VerticalGroup spaceBetweenElements={2}>
                          {selectedLayout.type === "custom_image" &&
                            selectedLayout.config.type === "custom_image" &&
                            selectedLayout.config.imageUrl != null && (
                              <Button
                                disabled={apiUpdating}
                                onClick={() =>
                                  onCustomTemplateEdit({
                                    config: {
                                      type: "custom_image",
                                      heading:
                                        selectedLayout.config.heading ?? "",
                                    },
                                  })
                                }
                              >
                                Remove Image
                              </Button>
                            )}

                          {selectedLayout.type ===
                            "custom_text_and_image_left" &&
                            selectedLayout.config.type ===
                              "custom_text_and_image_left" &&
                            selectedLayout.config.imageUrl != null && (
                              <Button
                                disabled={apiUpdating}
                                onClick={() =>
                                  onCustomTemplateEdit({
                                    config: {
                                      type: "custom_text_and_image_left",
                                      heading:
                                        selectedLayout.config.heading ?? "",
                                      body:
                                        (
                                          selectedLayout.config as API.ReportingLayoutCustomTextAndImageLeftConfig
                                        ).body ?? "",
                                    },
                                  })
                                }
                              >
                                Remove Image
                              </Button>
                            )}

                          {selectedLayout.type ===
                            "custom_text_and_image_right" &&
                            selectedLayout.config.type ===
                              "custom_text_and_image_right" &&
                            selectedLayout.config.imageUrl != null && (
                              <Button
                                disabled={apiUpdating}
                                onClick={() =>
                                  onCustomTemplateEdit({
                                    config: {
                                      type: "custom_text_and_image_right",
                                      heading:
                                        selectedLayout.config.heading ?? "",
                                      body:
                                        (
                                          selectedLayout.config as API.ReportingLayoutCustomTextAndImageRightConfig
                                        ).body ?? "",
                                    },
                                  })
                                }
                              >
                                Remove Image
                              </Button>
                            )}

                          {selectedLayout.type === "custom_video" &&
                            selectedLayout.config.type === "custom_video" &&
                            selectedLayout.config.videoUrl != null && (
                              <Button
                                disabled={apiUpdating}
                                onClick={() =>
                                  onCustomTemplateEdit({
                                    config: {
                                      type: "custom_video",
                                      heading:
                                        selectedLayout.config.heading ?? "",
                                    },
                                  })
                                }
                              >
                                Remove Video
                              </Button>
                            )}

                          <InlineGroup spaceBetweenElements={2} verticalCenter>
                            <Text>Enabled:</Text>
                            {apiUpdating && <LoadingSpinner />}
                            {!apiUpdating && (
                              <Toggle
                                checked={selectedLayout.enabled}
                                onUpdate={() =>
                                  onLayoutUpdate(selectedLayout.id, {
                                    enabled: !selectedLayout.enabled,
                                  })
                                }
                              />
                            )}
                          </InlineGroup>
                          {[
                            "custom_image",
                            "custom_video",
                            "custom_text",
                            "custom_text_and_image_left",
                            "custom_text_and_image_right",
                            "custom_slide",
                          ].includes(selectedLayout.type) && (
                            <Button
                              danger
                              data-test={`delete-template-${data.id}`}
                              onClick={() =>
                                setUIState((prevState) => ({
                                  ...prevState,
                                  deleteLayoutModalOpen: true,
                                }))
                              }
                            >
                              Delete
                            </Button>
                          )}
                          <EditSpeakerNotes
                            layout={selectedLayout}
                            onUpdate={onLayoutUpdate}
                          />
                        </VerticalGroup>
                        {[
                          "custom_image",
                          "custom_video",
                          "custom_text",
                          "custom_text_and_image_left",
                          "custom_text_and_image_right",
                          "custom_slide",
                        ].includes(selectedLayout.type) && (
                          <EditRoundRestrictions
                            reportingTemplateId={reportingTemplateId}
                            layout={selectedLayout}
                            onFinish={() => refresh()}
                          />
                        )}
                      </VerticalGroup>
                    </InlineGroup>
                  )}
                </div>
              </>
            )}
          </InlineGroup>
        )}
      </Container>
    </>
  );
}

export default ReportingTemplateDetails;
